import { FieldBase } from '@/components/form';
import { FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAnlageassistentFormContext } from '@/domain/mitarbeiter/features/anlageassistent/context/AnlageassistentFormContext';
import { useAnlageassistentContext } from '@/domain/mitarbeiter/features/anlageassistent/context/useAnlageassistentContext';
import { formatPhoneNumber } from '@/domain/mitarbeiter/features/anlageassistent/utils/formatPhoneNumber';
import { useTranslation } from 'react-i18next';
import { useValidateEmail } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useValidateEmail/useValidateEmail';
import { useEffect } from 'react';

// eslint-disable-next-line max-lines-per-function
export function Kontakt() {
  const { ctx } = useAnlageassistentContext();
  const { t } = useTranslation();
  const form = useAnlageassistentFormContext();
  const [validateEmail] = useValidateEmail();

  const validate = async ({ email }: { email: string | undefined }) => {
    if (!email) {
      form.setFieldMeta('person.kontakt.email', (prev) => ({
        ...prev,
        isTouched: true,
        warnings: undefined,
      }));
      return;
    }
    const res = await validateEmail({ email });

    if (
      res.data?.Anlageassistent_validateEmail.findings.find(
        (finding) =>
          finding.__typename ===
          'mitarbeiter_MitarbeiterEmailCheckFindingBlacklist',
      )
    ) {
      form.setFieldMeta('person.kontakt.email', (prev) => ({
        ...prev,
        isTouched: true,
        warnings: [
          {
            message: t(
              'domain.mitarbeiter.create.steps.person.kontakt.form.email.validationBlacklist',
            ),
          },
        ],
      }));
    } else {
      form.setFieldMeta('person.kontakt.email', (prev) => ({
        ...prev,
        isTouched: true,
        warnings: undefined,
      }));
    }
    form.setFieldValue('person.kontakt.email', email);
  };

  useEffect(() => {
    const email = form.state.values.person.kontakt.email;
    validate({ email });
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h2 className="text-neutrals-900 text-xl font-bold leading-7">
          {ctx.getCurrentSubStep()?.title}
        </h2>
        <p className="text-neutrals-800 text-sm font-normal leading-5">
          {t('domain.mitarbeiter.create.steps.person.kontakt.description')}
        </p>
      </div>

      <FieldGroup className="col-span-2 grid md:grid-cols-2 items-start">
        <form.AppField
          name="person.kontakt.email"
          validators={{
            onBlurAsync: async ({ value, fieldApi }) => {
              if (fieldApi.state.meta.errors) return;
              return await validate({ email: value });
            },
          }}>
          {(field) => {
            const isInvalid = !field.state.meta.isValid;
            return (
              <FieldBase
                label={t(
                  'domain.mitarbeiter.create.steps.person.kontakt.form.email.label',
                )}
                infoText={t(
                  'domain.mitarbeiter.create.steps.person.kontakt.form.email.info',
                )}>
                <Input
                  id={field.name}
                  name={field.name}
                  value={field.state.value || ''}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === '' ? undefined : e.target.value,
                    )
                  }
                  onBlur={() => {
                    field.setValue(field.state.value?.trim() || undefined);
                    field.handleBlur();
                  }}
                  aria-invalid={isInvalid}
                />
              </FieldBase>
            );
          }}
        </form.AppField>
        <div className="flex items-end gap-2">
          <form.AppField name="person.kontakt.mobilnummer">
            {(field) => {
              const isInvalid = !field.state.meta.isValid;
              return (
                <FieldBase
                  label={t(
                    'domain.mitarbeiter.create.steps.person.kontakt.form.phone.label',
                  )}>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="tel"
                    inputMode="tel"
                    value={field.state.value || ''}
                    onChange={(e) =>
                      field.handleChange(
                        e.target.value === '' ? undefined : e.target.value,
                      )
                    }
                    onBlur={() => {
                      const value = field.state.value;
                      if (value) {
                        field.setValue(formatPhoneNumber(value));
                      }
                      field.handleBlur();
                    }}
                    aria-invalid={isInvalid}
                  />
                </FieldBase>
              );
            }}
          </form.AppField>
        </div>
      </FieldGroup>
    </div>
  );
}
